/**
 * /locations  +  /service-areas — National "Popular Locations" hub.
 *
 * Feb 2026 · National SEO refactor. Historically this page was
 * Sunshine-Coast-only ("Water testing across the Sunshine Coast").
 * Now that we run a hierarchical location tree
 * (country → state → region → suburb) with `/locations/australia`
 * and `/locations/:state` hubs, this page becomes the
 * "Popular Locations" landing spot:
 *
 *   1. Hero  — national framing.
 *   2. States we operate in — tiles → /locations/:state
 *      (currently only Queensland is live).
 *   3. Regions with on-the-ground coverage — tiles →
 *      /locations/:state/:region (currently only Sunshine Coast).
 *   4. Popular suburbs — the existing suburb grid, retained to
 *      preserve suburb-level internal linking and the DRAFT-preview
 *      affordance for staff.
 *   5. Mail-out prompt — national CTA at the bottom.
 *
 * Canonical is `/locations` for both `/locations` and `/service-areas`
 * so PageRank consolidates. `/service-areas` stays in the sitemap and
 * nav ("Popular Locations") for continuity with older backlinks.
 *
 * Content quality pinning tests (see
 * backend/tests/test_service_area_removal_national_model.py) require:
 *   • The string "Popular Locations" is present.
 *   • The breadcrumb no longer uses the legacy hard-coded label.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Send, Globe2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { SchemaJsonLd, localBusinessSchema } from "@/lib/seo";
import { COUNTRIES, STATES, REGIONS as NAT_REGIONS } from "@/data/locationTree";
import RegionPartnerChip from "@/pages/locations/RegionPartnerChip";

const BASE_URL = SEO_DEFAULTS.baseUrl;

export default function LocationsHub() {
  // Canonical is always `/locations` so `/service-areas` consolidates
  // into the same URL for Google's canonicalisation pass.
  useSEO({
    title: "Water Testing Locations Across Australia — Aware Water Testing",
    description:
      "Popular locations, active states and mail-out coverage for Aware Water Testing. Independent laboratory water testing for every Australian home — rainwater, bore, tank and tap.",
    url: `${BASE_URL}/locations`,
  });

  // National taxonomy — states with at least one live region.
  const activeStates = Object.values(STATES).filter(
    (s) => s.regions && s.regions.length > 0,
  );
  const country = COUNTRIES.australia;
  const activeRegions = Object.values(NAT_REGIONS);

  return (
    <div className="min-h-screen bg-white" data-testid="locations-hub">
      <Header />
      <SchemaJsonLd id="jsonld-locations-org" data={localBusinessSchema()} />

      {/* Hero — national framing */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-14 sm:py-20 pt-28 sm:pt-32">
          <div className="mb-6 [&_*]:!text-cyan-100 [&_a:hover]:!text-white">
            {/* Sep 2026 — the <Breadcrumbs> component is the SINGLE
                source of BreadcrumbList JSON-LD. Passing the canonical
                url keeps the trail pointing at /locations even when the
                page is reached via the /service-areas alias. */}
            <Breadcrumbs items={[{ name: "Popular Locations", url: "/locations" }]} />
          </div>
          <Badge className="bg-cyan-100 text-cyan-900 mb-4" data-testid="locations-hub-badge">
            <Globe2 className="w-3 h-3 mr-1" /> Popular Locations · Australia-wide
          </Badge>
          <h1
            className="text-3xl sm:text-5xl font-bold tracking-tight"
            data-testid="locations-hub-h1"
          >
            Water testing across Australia
          </h1>
          <p className="mt-5 text-cyan-50 max-w-2xl text-[15px] sm:text-base leading-relaxed">
            {country.tagline} We currently operate on-the-ground partner
            coverage in the states listed below, and ship sample kits
            Australia-wide with pre-paid return postage — so wherever you are,
            you can get an independent laboratory water report the same way a customer
            down the street does. Pick a state to see live regions, or scroll
            for popular suburb pages.
          </p>
          <p className="mt-4 text-cyan-100 max-w-2xl text-[14px] sm:text-[15px] leading-relaxed">
            How mail-out works: book online — we post you a sampling kit with
            sterile bottles, step-by-step collection instructions and a
            pre-paid express-post return tube. You collect your water sample,
            drop the tube in any Australia Post box, and we email your
            report within the standard laboratory turnaround.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book-water-test">
              <Button
                className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-white h-11 px-6"
                data-testid="locations-hub-book-cta"
              >
                Book a water test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/mail-out-water-testing-kits">
              <Button
                variant="outline"
                className="rounded-full h-11 px-6 bg-transparent border-cyan-200 text-cyan-50 hover:bg-cyan-800/40"
                data-testid="locations-hub-mailout-cta"
              >
                <Send className="w-4 h-4 mr-2" /> Mail-out sample kit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* States we cover */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
              States we cover
            </h2>
            <div className="text-xs text-slate-500">
              {activeStates.length} live · more coming through 2026
            </div>
          </div>
          <p className="mt-2 text-slate-600 text-[14.5px] max-w-2xl">
            Live partner coverage in the states below, with mail-out kits
            available Australia-wide.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeStates.map((s) => (
              <Link
                key={s.slug}
                to={`/locations/${s.slug}`}
                data-testid={`locations-hub-state-${s.slug}`}
                className="group rounded-2xl bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md transition p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">
                      Active state · {s.abbr}
                    </div>
                    <div className="mt-1 text-2xl font-bold text-slate-900 group-hover:text-cyan-800">
                      {s.name}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition" />
                </div>
                <p className="mt-3 text-sm text-slate-600 line-clamp-2">
                  {s.tagline}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {s.regions.map((r) => (
                    <span
                      key={r}
                      className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5"
                    >
                      {r.replace(/-/g, " ")}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
            {/* Coming-soon tile — communicates roadmap without linking to empty pages */}
            <div
              className="rounded-2xl bg-white border border-dashed border-slate-300 p-6 flex flex-col justify-between"
              data-testid="locations-hub-coming-soon"
            >
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Coming through 2026
                </div>
                <div className="mt-1 text-lg font-semibold text-slate-800">
                  WA · SA · TAS · ACT
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Partner-led regional coverage rolling out. Mail-out kits are
                already available in every state — book online, we post you a
                sampling kit with a pre-paid express-post return tube, and you
                drop the tube in any mailbox to send your sample to the
                laboratory.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live regions — currently only Sunshine Coast */}
      {activeRegions.length > 0 && (
        <section className="py-14 bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
              Regions with on-the-ground coverage
            </h2>
            <p className="mt-2 text-slate-600 text-[14.5px] max-w-2xl">
              These regions have partner technicians available for local
              sampling and drop-off. Every other Australian address is served
              by our national mail-out kit.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeRegions.map((r) => {
                const state = STATES[r.state];
                const regionHref = `/locations/${state?.slug || "australia"}/${r.slug}`;
                return (
                  <div
                    key={r.slug}
                    data-testid={`locations-hub-region-${r.slug}`}
                    className="group rounded-2xl bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md transition p-6"
                  >
                    <Link to={regionHref} className="block">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">
                            Active region · Postcodes {r.postcodes_covered_range}
                          </div>
                          <div className="mt-1 text-2xl font-bold text-slate-900 group-hover:text-cyan-800">
                            {r.name}
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition" />
                      </div>
                      <p className="mt-3 text-sm text-slate-600 line-clamp-3">
                        {r.tagline}
                      </p>
                    </Link>
                    {/* Direct-to-partner chip (0/1/many partner states). */}
                    <RegionPartnerChip
                      state={state}
                      region={r}
                      regionHubHref={regionHref}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Popular suburbs section removed — the Sunshine-Coast-only
          suburb grid contradicted the national framing of this page.
          The same suburb cards still live on the Sunshine Coast
          RegionHub (`/locations/queensland/sunshine-coast`) where
          they belong. Internal linking to individual suburb pages
          is preserved via the RegionHub route from the "Regions
          with on-the-ground coverage" tiles above. */}

      {/* National mail-out CTA — closes the loop for visitors outside
          any active region. */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 px-3 py-1 text-xs font-semibold">
            <Send className="w-3.5 h-3.5" /> Not in an active region?
          </div>
          <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-slate-900">
            Sample from anywhere in Australia.
          </h2>
          <p className="mt-3 text-slate-600 text-[15px]">
            Book online — we post you a sampling kit with sterile bottles,
            step-by-step instructions and a pre-paid express-post return
            tube. You collect your water sample, drop the tube in any
            Australia Post box, and we email your report.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/book-water-test">
              <Button
                className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white h-11 px-6"
                data-testid="locations-hub-book-cta-bottom"
              >
                Book a water test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/mail-out-water-testing-kits">
              <Button
                variant="outline"
                className="rounded-full h-11 px-6"
                data-testid="locations-hub-mailout-cta-bottom"
              >
                How mail-out works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
